/**
 * ReviewSchema.jsx
 *
 * SEO component that injects schema.org structured data for reviews
 * Helps Google show star ratings in search results
 *
 * - Fetches review statistics from API
 * - Generates JSON-LD structured data
 * - Injects into <head> via React Helmet
 *
 * No props required - self-contained component
 */

import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

const ReviewSchema = () => {
  const [reviewData, setReviewData] = useState(null);

  useEffect(() => {
    const fetchReviewStats = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4001';
        const response = await fetch(`${apiUrl}/api/reviews/stats`);
        const data = await response.json();

        if (data.success && data.totalReviews > 0) {
          setReviewData(data);
        }
      } catch (error) {
        console.error('Error fetching review stats for schema:', error);
        // Fail silently - SEO enhancement only
      }
    };

    fetchReviewStats();
  }, []);

  // Don't render schema if no reviews yet
  if (!reviewData || reviewData.totalReviews === 0) {
    return null;
  }

  // Generate schema.org structured data
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'VideoGame',
    name: 'Qwixx Online',
    url: 'https://qwixx-online.nl',
    description:
      'Play Qwixx dice game online for free with friends. Multiplayer board game with solo, private, and public modes.',
    image: 'https://qwixx-online.nl/og-image.png',

    // Aggregate rating from real user reviews
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: reviewData.averageRating.toFixed(1),
      reviewCount: reviewData.totalReviews,
      bestRating: '5',
      worstRating: '1',
    },

    // Individual reviews (top 10 for SEO)
    review: reviewData.topReviews
      ? reviewData.topReviews.map((review) => ({
          '@type': 'Review',
          author: {
            '@type': 'Person',
            name: review.playerName || 'Anonymous',
          },
          datePublished: review.createdAt || new Date(review.timestamp).toISOString(),
          reviewBody: review.comment,
          reviewRating: {
            '@type': 'Rating',
            ratingValue: review.rating,
            bestRating: '5',
            worstRating: '1',
          },
        }))
      : [],

    // Game metadata
    gamePlatform: ['Web browser', 'Online'],
    genre: 'Board Game',
    playMode: 'MultiPlayer',
    numberOfPlayers: {
      '@type': 'QuantitativeValue',
      minValue: 2,
      maxValue: 8,
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
};

export default ReviewSchema;
